import {
	type FlagAction,
	type FlagConfiguration,
	type FlagMessage,
	type ShowFlagFn,
	toFlagId,
} from '@atlassian/jira-flags';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import { DEFAULT_APP_CONFIGURATION_URL, APP_POST_INSTALL_CONFIGURE_ACTIONS } from './constants';
import type { InstallationStatusConfig, ModalState } from './types';

const createIsModalStatePredicate =
	(suffix: string) =>
	(flagId?: string): boolean =>
		flagId?.endsWith(suffix) ?? false;

export const isModalStateSuccess = createIsModalStatePredicate('-install-success');

export const isModalStateError = createIsModalStatePredicate('-install-error');

export const isModalStateInProgress = createIsModalStatePredicate('-install-in-progress');

export const isModalStateFlag = (flagId?: string): boolean =>
	isModalStateInProgress(flagId) || isModalStateError(flagId) || isModalStateSuccess(flagId);

export const getModalState = (flagId?: string): ModalState => {
	if (isModalStateError(flagId)) {
		return 'FAIL';
	}

	if (isModalStateSuccess(flagId)) {
		return 'SUCCESS';
	}

	return 'IN_PROGRESS';
};

const getMessage = (message?: FlagMessage | string): string | undefined =>
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	typeof message === 'string' ? message : (message as MessageDescriptor).defaultMessage; // The type can mismatch because of how the flag object is created in the marketplace emcee package

export const createMarketplaceFlag = (flag: FlagConfiguration): FlagConfiguration => ({
	key: toFlagId(flag.id ?? ''),
	type: flag.type,
	title: {
		id: 'marketplace.app-install.flag-title',
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		defaultMessage: flag.title as string,
	},
	description: {
		id: 'marketplace.app-install.flag-description',
		defaultMessage: getMessage(flag.description),
	},
	actions: flag.actions?.map<FlagAction>((action, index) => ({
		...action,
		content: {
			id: `marketplace.app-install.action${index}`,
			// @ts-expect-error - TS2322 - Type 'FlagMessage' is not assignable to type 'string | undefined'. Type 'MessageDescriptorV2' is not assignable to type 'string'.
			defaultMessage: action.content,
		},
	})),
});

// Actions are identified by their `content` property
export const getAppPostInstallConfigureAction = (flag: FlagConfiguration): FlagAction | undefined =>
	flag.actions?.find((action) =>
		APP_POST_INSTALL_CONFIGURE_ACTIONS.includes(getMessage(action.content) ?? ''),
	);

export const createInstallationStatusConfig = (
	flag: FlagConfiguration,
): InstallationStatusConfig => ({
	modalState: getModalState(flag.id),
	configurationUrl: getAppPostInstallConfigureAction(flag)?.href ?? DEFAULT_APP_CONFIGURATION_URL,
	onTryAgain: flag.actions?.[0]?.onClick,
});

export const handleAppInstallFlagShow = ({
	flag,
	enablePostInstallModal,
	openInstallationStatusModal,
	closeInstallationStatusModal,
	showFlag,
}: {
	flag: FlagConfiguration;
	enablePostInstallModal: boolean;
	openInstallationStatusModal: (flag: FlagConfiguration) => void;
	closeInstallationStatusModal: () => void;
	showFlag: ShowFlagFn;
}): void => {
	if (!enablePostInstallModal || !isModalStateFlag(flag.id)) {
		showFlag(createMarketplaceFlag(flag));
		return;
	}

	// only show post-install success modal if the flag has post-install actions
	if (isModalStateSuccess(flag.id)) {
		if (getAppPostInstallConfigureAction(flag)) {
			openInstallationStatusModal(flag);
			return;
		}

		showFlag(createMarketplaceFlag(flag));
		closeInstallationStatusModal();
		return;
	}

	openInstallationStatusModal(flag);
};
